"use client"

// components/ui/ExportMenu.tsx
// Primitivo del DS para exportar reportes: menú "Exportar ▾" con opciones
// (PDF / Excel / CSV / …), estado ocupado y error inline. Construido sobre
// <Menu>. Las opciones son callbacks — pueden ser client-side (downloadCsv)
// o pegarle a un API (downloadExport); el menú maneja loading/error parejo.
//
// Spec (S2 del plan de exports): un solo punto de export por reporte en vez
// de botones sueltos por formato; los errores se muestran, nunca descargas
// corruptas silenciosas.
//
// Dos cosas más, del reporte del dueño (2026-09-17) "no me deja exportar":
//
//   - CONFIRMACIÓN. En el shell nativo el archivo NO cae en una carpeta de
//     Descargas: se guarda y se abre la hoja de compartir. Si el menú se cierra
//     mudo, el operador cree que no pasó nada — que es justo lo que venía
//     pasando. Por eso `run` devuelve el DeliveryMode y acá se dice dónde quedó.
//
//   - CORREO. Mientras la tablet no tenga el build con el plugin Share, el
//     correo es la única salida real; y para el contador lo es siempre. El
//     panel cambia a un formulario corto en vez de abrir otro modal.
import { useState } from "react"
import { useTranslations } from "next-intl"
import { Menu } from "./Menu"
import { Button } from "./Button"
import { Input } from "./Input"
import type { DeliveryMode } from "@/lib/export/deliverFile"

export interface ExportMenuOption {
  key: string
  /** Etiqueta de la opción, p.ej. "PDF", "Excel (.xlsx)", "CSV". */
  label: string
  /** Acción. Si rechaza/lanza, el mensaje del Error se muestra inline.
   *  Devolver el DeliveryMode permite confirmar dónde quedó el archivo. */
  run: () => void | DeliveryMode | Promise<void | DeliveryMode>
}

export interface ExportMenuEmail {
  /** Formatos que se pueden mandar adjuntos. El primero es el preseleccionado. */
  formats: { key: string; label: string }[]
  /** Correo sugerido (el buzón del negocio). El dueño lo puede cambiar. */
  defaultTo?: string
  /** Manda el reporte y devuelve la dirección a la que llegó. */
  send: (format: string, to: string) => Promise<string>
}

export interface ExportMenuProps {
  options: ExportMenuOption[]
  /** Habilita el paso "Enviar por correo" dentro del mismo menú. */
  email?: ExportMenuEmail
  /** Etiqueta del trigger (default "Exportar"). */
  label?: string
  "aria-label"?: string
}

/** Qué decirle al operador según por dónde salió el archivo. */
function successMessage(mode: void | DeliveryMode, savedOnDevice: string): string | null {
  return mode === "native-share" ? savedOnDevice : null
}

export function ExportMenu({ options, email, label: labelProp, ...rest }: ExportMenuProps) {
  const t = useTranslations("admin.exportMenu")
  const label = labelProp ?? t("triggerLabel")
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [view, setView] = useState<"options" | "email">("options")
  const [emailTo, setEmailTo] = useState(email?.defaultTo ?? "")
  const [emailFormat, setEmailFormat] = useState(email?.formats[0]?.key ?? "")

  function reset() {
    setError(null)
    setNotice(null)
    setView("options")
    setBusyKey(null)
  }

  async function handle(option: ExportMenuOption, close: () => void) {
    setError(null)
    setNotice(null)
    setBusyKey(option.key)
    try {
      const mode = await option.run()
      const message = successMessage(mode, t("savedOnDevice"))
      // Solo se queda abierto cuando hay algo que decir. En web la descarga es
      // visible sola y dejar el panel abierto sería ruido.
      if (message) setNotice(message)
      else close()
    } catch (e) {
      setError(e instanceof Error ? e.message : t("exportFailed"))
    } finally {
      setBusyKey(null)
    }
  }

  async function handleSend() {
    if (!email) return
    setError(null)
    setNotice(null)
    setBusyKey("email")
    try {
      const sentTo = await email.send(emailFormat, emailTo.trim())
      setNotice(t("sentTo", { email: sentTo }))
    } catch (e) {
      setError(e instanceof Error ? e.message : t("sendFailed"))
    } finally {
      setBusyKey(null)
    }
  }

  const busy = busyKey !== null

  return (
    <div className="inline-flex flex-col items-end">
      <Menu
        aria-label={rest["aria-label"] ?? t("ariaLabel")}
        align="end"
        trigger={
          <span className="inline-flex items-center gap-2">
            <svg viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5" aria-hidden>
              <path d="M8 1a.5.5 0 0 1 .5.5v6.793l2.646-2.647a.5.5 0 0 1 .708.708l-3.5 3.5a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L7.5 8.293V1.5A.5.5 0 0 1 8 1zM0 13.5a.5.5 0 0 1 .5-.5h15a.5.5 0 0 1 0 1H.5a.5.5 0 0 1-.5-.5z" />
            </svg>
            {label}
            <span className="text-[9px] leading-none" aria-hidden>▾</span>
          </span>
        }
        triggerClassName="no-print inline-flex items-center px-4 py-2 bg-[var(--redy-surface)] border border-[var(--redy-border)] rounded-xl text-[13px] font-semibold text-[var(--redy-secondary)] hover:border-[#1A1A1A]/30 hover:text-[var(--redy-charcoal)] transition-colors cursor-pointer"
        panelClassName="min-w-[180px]"
      >
        {close => (
          <>
            {view === "options" && (
              <>
                {options.map(o => (
                  <button
                    key={o.key}
                    role="menuitem"
                    type="button"
                    disabled={busy}
                    onClick={() => handle(o, close)}
                    className="w-full text-left px-3 py-2 rounded-[10px] text-[13px] font-medium text-[var(--redy-charcoal)] hover:bg-[var(--redy-surface-2)] transition-colors cursor-pointer disabled:opacity-50"
                  >
                    {busyKey === o.key ? t("generating") : o.label}
                  </button>
                ))}
                {email && (
                  <>
                    <span className="block h-px my-1 bg-[var(--redy-border-l)]" aria-hidden />
                    <button
                      role="menuitem"
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setError(null)
                        setNotice(null)
                        setView("email")
                      }}
                      className="w-full text-left px-3 py-2 rounded-[10px] text-[13px] font-medium text-[var(--redy-charcoal)] hover:bg-[var(--redy-surface-2)] transition-colors cursor-pointer disabled:opacity-50"
                    >
                      {t("emailOption")}
                    </button>
                  </>
                )}
              </>
            )}

            {view === "email" && email && (
              <div className="p-1 min-w-[260px]">
                <p className="px-2 pt-1 pb-2 text-[11px] font-bold tracking-[0.16em] uppercase text-[var(--redy-red)]">
                  {t("emailTitle")}
                </p>

                <div className="px-2 pb-2 flex gap-1.5">
                  {email.formats.map(f => (
                    <button
                      key={f.key}
                      type="button"
                      disabled={busy}
                      aria-pressed={emailFormat === f.key}
                      onClick={() => setEmailFormat(f.key)}
                      className={`px-2.5 py-1.5 rounded-[8px] text-[12px] font-semibold border transition-colors cursor-pointer disabled:opacity-50 ${
                        emailFormat === f.key
                          ? "border-[var(--redy-charcoal)] text-[var(--redy-charcoal)] bg-[var(--redy-surface-2)]"
                          : "border-[var(--redy-border)] text-[var(--redy-secondary)] hover:text-[var(--redy-charcoal)]"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                <div className="px-2 pb-2">
                  <label htmlFor="export-email-to" className="sr-only">
                    {t("emailToLabel")}
                  </label>
                  {/* Vacío es un valor válido: el servidor resuelve el buzón del
                      negocio. Exigir que lo escriban convertiría ese default en
                      código muerto, y el dueño rara vez quiere otra dirección
                      que no sea la suya o la del contador. */}
                  <Input
                    id="export-email-to"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    inputSize="sm"
                    placeholder={t("emailToPlaceholder")}
                    value={emailTo}
                    disabled={busy}
                    onChange={e => setEmailTo(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter" && !busy) {
                        e.preventDefault()
                        void handleSend()
                      }
                    }}
                  />
                </div>

                <div className="px-2 pb-1 flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="primary"
                    loading={busyKey === "email"}
                    disabled={busy}
                    onClick={() => void handleSend()}
                  >
                    {t("send")}
                  </Button>
                  <Button size="sm" variant="ghost" disabled={busy} onClick={reset}>
                    {t("back")}
                  </Button>
                </div>
              </div>
            )}

            {notice && (
              <p className="px-3 pt-2 pb-1 text-[12px] font-medium text-[var(--redy-success)]">{notice}</p>
            )}
          </>
        )}
      </Menu>
      {error && (
        <p className="text-[12px] font-medium text-[var(--redy-error)] mt-2 text-right max-w-[280px]">{error}</p>
      )}
    </div>
  )
}
